import { Component, type ReactNode } from 'react';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { ErrorReportCard } from '@/components/ErrorReportPanel';
import { Button } from '@/components/ui/button';
import { openErrorReport, reportError, type ErrorReport } from '@/lib/errorReport';
import { isCustomerFacingPath } from '@/lib/marker';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  level?: 'page' | 'section' | 'block';
  onReset?: () => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  /** Alleen op teampagina's; klanten zien geen technische details. */
  report: ErrorReport | null;
}

const REPORT_TITLES = {
  page: 'De pagina liep vast',
  section: 'Een onderdeel kon niet geladen worden',
  block: 'Een blok kon niet geladen worden',
};

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, report: null };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    if (isCustomerFacingPath()) {
      console.error('[ErrorBoundary]', error, info.componentStack);
      return;
    }
    const title = REPORT_TITLES[this.props.level ?? 'section'];
    this.setState({ report: reportError(title, error, info.componentStack ?? undefined) });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, report: null });
    this.props.onReset?.();
  };

  renderDetailsButton() {
    const { report } = this.state;
    if (!report) return null;
    return (
      <Button size="sm" variant="ghost" onClick={() => openErrorReport(report.code)}>
        Details ({report.code})
      </Button>
    );
  }

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    if (this.props.fallback) {
      return this.props.fallback;
    }

    const level = this.props.level ?? 'section';

    if (level === 'block') {
      return (
        <div className="flex h-full min-h-[120px] flex-col items-center justify-center rounded border border-dashed border-red-300 bg-red-50 p-3">
          <AlertCircle className="mb-1 h-8 w-8 text-destructive" />
          <p className="mb-2 text-sm text-destructive">Dit blok kon niet geladen worden</p>
          <div className="flex flex-wrap justify-center gap-1">
            <Button size="sm" variant="outline" onClick={this.handleReset}>
              <RefreshCw className="mr-1 h-3 w-3" /> Opnieuw proberen
            </Button>
            {this.renderDetailsButton()}
          </div>
        </div>
      );
    }

    if (level === 'page') {
      return (
        <div className="flex min-h-screen flex-col items-center justify-center bg-muted p-4">
          <AlertCircle className="mb-2 h-16 w-16 text-destructive" />
          <h2 className="mb-2 text-xl font-semibold">Er is iets misgegaan</h2>
          <p className="mb-3 max-w-[400px] text-center text-muted-foreground">
            De pagina heeft een onverwachte fout ondervonden. Probeer het opnieuw of ga terug naar het offerte-overzicht.
          </p>
          <div className="flex gap-2">
            <Button onClick={this.handleReset}>
              <RefreshCw className="mr-1 h-4 w-4" /> Opnieuw proberen
            </Button>
            <Button variant="outline" onClick={() => (window.location.href = '/quotes')}>
              Naar offerte-overzicht
            </Button>
          </div>
          {this.state.report ? (
            <ErrorReportCard report={this.state.report} className="mt-6" />
          ) : (
            import.meta.env.DEV &&
            this.state.error && (
              <div className="mt-4 max-w-[600px] overflow-auto rounded bg-muted p-2">
                <pre className="whitespace-pre-wrap text-[11px]">{this.state.error.message}</pre>
              </div>
            )
          )}
        </div>
      );
    }

    return (
      <div className="flex min-h-[200px] flex-col items-center justify-center rounded-lg border border-red-200 bg-white p-4">
        <AlertCircle className="mb-2 h-12 w-12 text-destructive" />
        <h3 className="mb-2 text-lg font-semibold">Fout bij laden</h3>
        <p className="mb-2 text-sm text-muted-foreground">Dit onderdeel kon niet geladen worden.</p>
        <div className="flex flex-wrap justify-center gap-2">
          <Button variant="outline" onClick={this.handleReset}>
            <RefreshCw className="mr-1 h-4 w-4" /> Opnieuw proberen
          </Button>
          {this.renderDetailsButton()}
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
